import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import carGurusAPIService from '../../services/carGurusAPIService';
import carGurusRealUploadService from '../../services/carGurusRealUploadService';
import { 
  FiBarChart2, FiUploadCloud, FiDownload, FiExternalLink, 
  FiRefreshCw, FiCopy, FiCheckCircle, FiPackage, FiEye,
  FiUsers, FiTrendingUp, FiAlertCircle
} from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const CarGurusComplete = () => {
  const [activeTab, setActiveTab] = useState('stats');
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [statsData, setStatsData] = useState(null);
  const [feedUrl, setFeedUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [vinStats, setVinStats] = useState([]);

  useEffect(() => {
    loadInitialData();
    setDefaultDateRange();
  }, []);

  const setDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
  };

  const loadInitialData = async () => {
    setLoading(true);
    
    // Load vehicles
    const vehicleData = await carGurusRealUploadService.fetchVehicles();
    setVehicles(vehicleData);
    
    // Load stats if we have dates
    if (dateRange.start && dateRange.end) {
      await loadStats();
    }
    
    setLoading(false);
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      // Get dealer stats using the correct API
      const dealerStats = await carGurusAPIService.getDealerStatsAggregate(
        new Date(dateRange.start),
        new Date(dateRange.end)
      );
      
      // Get VIN stats for first 10 vehicles
      const vins = vehicles.slice(0, 10).map(v => v.vin).filter(Boolean);
      let vinStatsData = [];
      
      if (vins.length > 0) {
        const vinResponse = await carGurusAPIService.getVINStatsBasic(
          vins,
          new Date(dateRange.start),
          new Date(dateRange.end)
        );
        if (vinResponse.success && vinResponse.data) {
          // Check for vin_stats in response (per API documentation)
          vinStatsData = vinResponse.data.vin_stats || vinResponse.data;
          vinStatsData = Array.isArray(vinStatsData) ? vinStatsData : [vinStatsData];
        }
      }
      
      setStatsData(dealerStats);
      setVinStats(vinStatsData);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
    setLoading(false);
  };

  const generateFeedUrl = async () => {
    setLoading(true);
    const result = await carGurusRealUploadService.generateFeedUrl();
    if (result.success) {
      setFeedUrl(result);
    }
    setLoading(false);
  };

  const downloadCSV = async () => {
    await carGurusRealUploadService.downloadCSV();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header with Tabs */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">CarGurus Integration</h2>
        
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('stats')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'stats'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <SafeIcon icon={FiBarChart2} className="w-4 h-4 inline mr-2" />
              Performance Stats
            </button>
            <button
              onClick={() => setActiveTab('upload')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'upload'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <SafeIcon icon={FiUploadCloud} className="w-4 h-4 inline mr-2" />
              Upload Inventory
            </button>
          </nav>
        </div>

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div className="space-y-6">
            {/* Date Range Selector */}
            <div className="flex items-end space-x-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <button
                onClick={loadStats}
                disabled={loading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                <SafeIcon icon={loading ? FiRefreshCw : FiBarChart2} 
                  className={`w-4 h-4 inline mr-2 ${loading ? 'animate-spin' : ''}`} />
                Load Stats
              </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Total Leads</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {statsData?.data?.total_leads || statsData?.data?.totalLeads || '-'}
                    </p>
                  </div>
                  <SafeIcon icon={FiUsers} className="w-8 h-8 text-blue-500" />
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Total Views</p>
                    <p className="text-2xl font-bold text-green-900">
                      {statsData?.data?.total_impressions || statsData?.data?.totalImpressions || '-'}
                    </p>
                  </div>
                  <SafeIcon icon={FiEye} className="w-8 h-8 text-green-500" />
                </div>
              </div>

              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Total Clicks</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {statsData?.data?.total_clicks || statsData?.data?.totalClicks || '-'}
                    </p>
                  </div>
                  <SafeIcon icon={FiTrendingUp} className="w-8 h-8 text-purple-500" />
                </div>
              </div>

              <div className="bg-orange-50 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-orange-600 font-medium">Active Vehicles</p>
                    <p className="text-2xl font-bold text-orange-900">{vehicles.length}</p>
                  </div>
                  <SafeIcon icon={FiPackage} className="w-8 h-8 text-orange-500" />
                </div>
              </div>
            </div>

            {/* VIN Performance Table */}
            {vinStats.length > 0 && (
              <div className="bg-white border rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 p-4 border-b">
                  Vehicle Performance
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          VIN
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          VDP Views
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          SRP Views
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email Leads
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Phone Leads
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {vinStats.map((stat, idx) => (
                        <tr key={idx}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {stat.vin || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {stat.vdp_views || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {stat.srp_views || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {stat.email_leads || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {stat.phone_leads || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* API Info */}
            <div className={`border rounded-lg p-4 ${
              statsData?.isMock ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
            }`}>
              <p className={`text-sm ${statsData?.isMock ? 'text-red-800' : 'text-green-800'}`}>
                <SafeIcon icon={FiAlertCircle} className="w-4 h-4 inline mr-2" />
                <strong>Data Status:</strong> {statsData?.isMock ? '⚠️ Using MOCK data' : '✅ Using LIVE data from CarGurus'}
                {statsData?.error && (
                  <span className="block mt-2 text-xs">
                    Error: {statsData.error}
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Vehicles Ready for Upload</p>
                  <p className="text-2xl font-bold text-blue-900">{vehicles.length}</p>
                </div>
                <div className="space-x-4">
                  <button
                    onClick={downloadCSV}
                    disabled={loading || vehicles.length === 0}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
                    Download CSV
                  </button>
                  <button
                    onClick={generateFeedUrl}
                    disabled={loading || vehicles.length === 0}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <SafeIcon icon={FiUploadCloud} className="w-4 h-4 mr-2" />
                    Generate Feed URL
                  </button>
                </div>
              </div>
            </div>

            {/* Feed URL Result */}
            {feedUrl && feedUrl.success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <h3 className="font-bold text-green-900 mb-2">Feed URL Generated!</h3>
                <div className="flex items-center space-x-2 mb-4">
                  <input
                    type="text"
                    value={feedUrl.feedUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border rounded bg-white"
                  />
                  <button
                    onClick={() => copyToClipboard(feedUrl.feedUrl)}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    <SafeIcon icon={copied ? FiCheckCircle : FiCopy} className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm text-green-700">
                  Use this URL in CarGurus Dealer Portal to set up automatic daily syncs
                </p>
              </div>
            )}

            {/* Upload Instructions */}
            <div className="border rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">How to Upload Your Inventory</h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <span className="bg-primary-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                    1
                  </span>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Generate Your Inventory</h4>
                    <p className="text-gray-600 text-sm mt-1">
                      Click "Download CSV" for manual upload or "Generate Feed URL" for automatic syncing
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <span className="bg-primary-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                    2
                  </span>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Go to CarGurus Dealer Portal</h4>
                    <p className="text-gray-600 text-sm mt-1">
                      Visit{' '}
                      <a 
                        href="https://wwws.cargurus.com/dealer_admin/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline inline-flex items-center"
                      >
                        https://wwws.cargurus.com/dealer_admin/
                        <SafeIcon icon={FiExternalLink} className="w-3 h-3 ml-1" />
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <span className="bg-primary-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                    3
                  </span>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Upload Your Inventory</h4>
                    <p className="text-gray-600 text-sm mt-1">
                      Navigate to Inventory → Inventory Feed and either upload the CSV or set up the feed URL
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Support Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Need Help?</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Contact: Stan Franson</p>
                  <p className="text-gray-600">Email: support@cargurus.com</p>
                </div>
                <div>
                  <p className="text-gray-600">Phone: 800-227-4878</p>
                  <p className="text-gray-600">Dealer ID: paradise-ds</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default CarGurusComplete;
